/* eslint-disable fp/no-mutation, fp/no-mutating-methods */
type GoogleTagManagerLoaderOptions = {
  browserWindow?: Window;
  dataLayerName?: string;
  documentRef?: Document;
  gtmId: string;
};

type CustomerIoLoaderOptions = {
  browserWindow?: Window;
  documentRef?: Document;
  siteId: string;
};

const GTM_SCRIPT_ID = 'duckbill-gtm-script';
const CUSTOMER_IO_SCRIPT_ID = 'cio-tracker';

export const isValidMarketingId = (id: string): boolean => Boolean(id) && !id.startsWith('%VITE_');

const getGlobalArray = (browserWindow: Window, key: string): unknown[] => {
  const target = browserWindow as Record<string, unknown> & Window;
  const existing = target[key];
  const value = Array.isArray(existing) ? existing : [];
  target[key] = value;
  return value;
};

const getFirstScript = (documentRef: Document): HTMLScriptElement | undefined =>
  documentRef.getElementsByTagName('script')[0];

const insertBeforeFirstScript = (documentRef: Document, script: HTMLScriptElement): boolean => {
  const firstScript = getFirstScript(documentRef);

  if (!firstScript?.parentNode) {
    return false;
  }

  firstScript.parentNode.insertBefore(script, firstScript);
  return true;
};

const hasScript = (documentRef: Document, id: string): boolean => Boolean(documentRef.getElementById(id));

export const injectGoogleTagManagerScript = ({
  browserWindow = window,
  dataLayerName = 'dataLayer',
  documentRef = document,
  gtmId,
}: GoogleTagManagerLoaderOptions): boolean => {
  const dataLayer = getGlobalArray(browserWindow, dataLayerName);

  if (!isValidMarketingId(gtmId) || hasScript(documentRef, GTM_SCRIPT_ID)) {
    return false;
  }

  dataLayer.push({ event: 'gtm.js', 'gtm.start': new Date().getTime() });
  browserWindow.duckbillMarketingHtmlBegin?.('GTM');

  const script = documentRef.createElement('script');
  const dataLayerParam = dataLayerName !== 'dataLayer' ? `&l=${encodeURIComponent(dataLayerName)}` : '';
  script.async = true;
  script.id = GTM_SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}${dataLayerParam}`;
  script.addEventListener('load', () => {
    browserWindow.duckbillMarketingHtmlFinished?.('GTM', 'success');
  });
  script.addEventListener('error', () => {
    browserWindow.duckbillMarketingHtmlFinished?.('GTM', 'error');
  });

  return insertBeforeFirstScript(documentRef, script);
};

export const injectCustomerIoScript = ({
  browserWindow = window,
  documentRef = document,
  siteId,
}: CustomerIoLoaderOptions): boolean => {
  if (!isValidMarketingId(siteId) || hasScript(documentRef, CUSTOMER_IO_SCRIPT_ID)) {
    return false;
  }

  browserWindow.duckbillMarketingHtmlBegin?.('Customer.io');

  const script = documentRef.createElement('script');
  script.async = true;
  script.id = CUSTOMER_IO_SCRIPT_ID;
  script.setAttribute('data-site-id', siteId);
  script.setAttribute('data-use-array-params', 'true');
  script.setAttribute('data-use-in-app', 'true');
  script.src = 'https://assets.customer.io/assets/track.js';
  script.addEventListener('load', () => {
    browserWindow.duckbillMarketingHtmlFinished?.('Customer.io', 'success');
  });
  script.addEventListener('error', () => {
    browserWindow.duckbillMarketingHtmlFinished?.('Customer.io', 'error');
  });

  return insertBeforeFirstScript(documentRef, script);
};
